import { safe } from "@orpc/client";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import z from "zod";
import { useAppForm } from "@/components/form";
import { ComboboxLanguage } from "@/components/forms/combobox-language";
import {
	Field,
	FieldContent,
	FieldDescription,
	FieldGroup,
	FieldLabel,
	FieldTitle,
} from "@/components/ui/field";
import {
	FormCard,
	FormCardContent,
	FormCardHeader,
	FormCardTitle,
} from "@/components/ui/form-card";
import { InputGroup, InputGroupAddon, InputGroupText } from "@/components/ui/input-group";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { EpisodeTypeMeta } from "@/enums/episode-types";
import { orpc, validationErrors } from "@/orpc/client";

export const Route = createFileRoute("/_app/episodes/$episodeId/")({
	component: Page,
	params: z.object({
		episodeId: z.coerce.number(),
	}),
	async loader({ context, params, preload }) {
		if (!preload) {
			return;
		}

		context.queryClient.prefetchQuery(
			orpc.episodes.show.queryOptions({
				input: {
					episodeId: params.episodeId,
				},
			}),
		);
	},
});

function Page() {
	const params = Route.useParams();

	const { data: episode } = useSuspenseQuery(
		orpc.episodes.show.queryOptions({
			input: {
				episodeId: params.episodeId,
			},
		}),
	);

	const updateMutation = useMutation(
		orpc.episodes.updateDetails.mutationOptions({
			onSuccess: (_data, _payload, _result, context) => {
				context.client.invalidateQueries({ queryKey: orpc.episodes.key() });
				toast.success("Episodes updated successfully.");
			},
			onError: () => {
				toast.error("Failed to update this episode.");
			},
		}),
	);

	const form = useAppForm({
		defaultValues: {
			SeasonNum: episode.SeasonNum || null,
			EpisodeNum: episode.EpisodeNum,
			Title: episode.Title || "",
			Description: episode.Description || "",
			HomepageUrl: episode.HomepageUrl || "",
			LanguageCode: episode.LanguageCode,
			TypeID: episode.TypeID,
			IsExplicit: episode.IsExplicit,
			PublishDate: episode.PublishDate || null,
			ExpiryDate: episode.ExpiryDate || null,
			Summary: episode.Summary || "",
		},
		canSubmitWhenInvalid: true,
		validators: {
			onSubmitAsync: async ({ value }) => {
				const { error } = await safe(
					updateMutation.mutateAsync({
						...value,
						ID: episode.ID,
					}),
				);

				if (error) {
					return validationErrors(error) ?? "An error occurred while updating the episode.";
				}
			},
		},
	});

	return (
		<form.AppForm>
			<form
				className="relative"
				id={form.formId}
				onSubmit={(e) => {
					e.preventDefault();
					e.stopPropagation();
					form.handleSubmit();
				}}
			>
				<div className="grid grid-cols-12 gap-6 px-4 lg:px-6">
					<FormCard className="col-span-12" disabled={updateMutation.isPending}>
						<FormCardHeader>
							<FormCardTitle>Episode Info</FormCardTitle>
						</FormCardHeader>
						<FormCardContent>
							<FieldGroup>
								<form.AppField
									name="Title"
									children={(field) => (
										<field.FormItem className="col-span-12">
											<field.FormLabel>Title</field.FormLabel>
											<InputGroup>
												<field.FormGroupInput />
												<InputGroupAddon align="block-end">
													<InputGroupText className="tabular-nums">
														{field.state.value.length}/500 characters
													</InputGroupText>
												</InputGroupAddon>
											</InputGroup>
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="Summary"
									children={(field) => (
										<field.FormItem className="col-span-12">
											<field.FormLabel>Summary</field.FormLabel>
											<InputGroup>
												<field.FormTextEditor className="max-h-[200px] border-0 shadow-none" />
												<InputGroupAddon align="block-end">
													<InputGroupText className="tabular-nums">
														{field.state.value.length}/4000 characters
													</InputGroupText>
												</InputGroupAddon>
											</InputGroup>
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="Description"
									children={(field) => (
										<field.FormItem className="col-span-12">
											<field.FormLabel>Description</field.FormLabel>
											<InputGroup>
												<field.FormTextEditor className="border-0 shadow-none" />
												<InputGroupAddon align="block-end">
													<InputGroupText className="tabular-nums">
														{field.state.value.length}/4000 characters
													</InputGroupText>
												</InputGroupAddon>
											</InputGroup>
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="HomepageUrl"
									children={(field) => (
										<field.FormItem className="col-span-12">
											<field.FormLabel>Homepage URL</field.FormLabel>
											<field.FormInput type="url" />
											<field.FormError />
										</field.FormItem>
									)}
								/>
							</FieldGroup>
						</FormCardContent>
					</FormCard>
					<FormCard className="col-span-12" disabled={updateMutation.isPending}>
						<FormCardHeader>
							<FormCardTitle>Episode Content</FormCardTitle>
						</FormCardHeader>
						<FormCardContent>
							<FieldGroup>
								<form.AppField
									name="LanguageCode"
									children={(field) => (
										<field.FormItem
											id={`${field.form.formId}-episode-language`}
											className="col-span-6"
										>
											<field.FormLabel>Language</field.FormLabel>
											<ComboboxLanguage
												id={`${field.form.formId}-episode-language`}
												value={field.state.value}
												onChange={(value) => field.handleChange(value)}
												onBlur={field.handleBlur}
												isInvalid={field.state.meta.isTouched && !field.state.meta.isValid}
											/>
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="IsExplicit"
									children={(field) => (
										<field.FormItem
											id={`${field.form.formId}-episode-explicit`}
											className="col-span-6"
										>
											<field.FormLabel>Explicit Content</field.FormLabel>
											<RadioGroup
												className="flex flex-row gap-2"
												value={field.state.value}
												onValueChange={field.handleChange}
												onBlur={field.handleBlur}
											>
												<FieldLabel>
													<Field orientation="horizontal" className="h-8 py-1.5!">
														<FieldContent>
															<FieldTitle>Yes</FieldTitle>
														</FieldContent>
														<RadioGroupItem value={true} />
													</Field>
												</FieldLabel>
												<FieldLabel>
													<Field orientation="horizontal" className="h-8 py-1.5!">
														<FieldContent>
															<FieldTitle>No</FieldTitle>
														</FieldContent>
														<RadioGroupItem value={false} />
													</Field>
												</FieldLabel>
											</RadioGroup>
											<field.FormError />
										</field.FormItem>
									)}
								/>
							</FieldGroup>
						</FormCardContent>
					</FormCard>
					<FormCard className="col-span-12" disabled={updateMutation.isPending}>
						<FormCardHeader>
							<FormCardTitle>Episode Publication</FormCardTitle>
						</FormCardHeader>
						<FormCardContent>
							<FieldGroup>
								<form.AppField
									name="PublishDate"
									children={(field) => (
										<field.FormItem
											id={`${field.form.formId}-episode-publish-date`}
											className="col-span-6"
										>
											<field.FormLabel>Publish Date</field.FormLabel>
											<field.FormDateTimePicker />
											<field.FormError />
										</field.FormItem>
									)}
								/>
								<form.AppField
									name="IsExplicit"
									children={(field) => (
										<field.FormItem
											id={`${field.form.formId}-channel-explicit`}
											className="col-span-6"
										>
											<field.FormLabel>Expiration Date</field.FormLabel>
											<field.FormDateTimePicker />
											<field.FormError />
										</field.FormItem>
									)}
								/>
							</FieldGroup>
						</FormCardContent>
					</FormCard>
					<FormCard className="col-span-12" disabled={updateMutation.isPending}>
						<FormCardHeader>
							<FormCardTitle>Episode Type</FormCardTitle>
						</FormCardHeader>
						<FormCardContent>
							<FieldGroup className="col-span-12">
								<form.AppField
									name="TypeID"
									children={(field) => (
										<RadioGroup
											value={field.state.value}
											onValueChange={field.handleChange}
											onBlur={field.handleBlur}
											className="col-span-full"
										>
											{Object.values(EpisodeTypeMeta).map((episodeType) => (
												<FieldLabel key={episodeType.id} htmlFor={`type-${episodeType.id}`}>
													<Field orientation="horizontal">
														<FieldContent>
															<FieldTitle>{episodeType.label}</FieldTitle>
															<FieldDescription className="text-xs">
																{episodeType.description}
															</FieldDescription>
														</FieldContent>
														<RadioGroupItem value={episodeType.id} id={`type-${episodeType.id}`} />
													</Field>
												</FieldLabel>
											))}
										</RadioGroup>
									)}
								/>
							</FieldGroup>
						</FormCardContent>
					</FormCard>
					<form.FormSaveBar />
				</div>
				<form.FormBottomBar />
			</form>
		</form.AppForm>
	);
}
